import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CallContext = createContext(null);
const WS_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

export function CallProvider({ children }) {
  const { user, token } = useAuth();

  const [callState, setCallState] = useState('idle'); // idle | calling | ringing | active
  const [callType, setCallType] = useState(null);     // audio | video
  const [peerId, setPeerId] = useState(null);
  const [peerName, setPeerName] = useState(null);
  const [isIncoming, setIsIncoming] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false); // iOS autoplay-blocked signal

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const timerRef = useRef(null);
  const pendingCandidates = useRef([]);
  // Refs mirror state so closures (like cleanup + signal handler) always see latest values
  const peerIdRef = useRef(null);
  const localStreamRef = useRef(null);
  useEffect(() => { peerIdRef.current = peerId; }, [peerId]);
  useEffect(() => { localStreamRef.current = localStream; }, [localStream]);

  // ── WebSocket connection (stable deps: only userId + token primitives) ──
  const userId = user?.id;
  useEffect(() => {
    if (!userId || !token) return;

    let stopped = false;
    let heartbeatTimer = null;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/api/ws/call/${userId}?token=${token}`);

      ws.onopen = () => {
        setWsConnected(true);
        clearInterval(heartbeatTimer);
        heartbeatTimer = setInterval(() => {
          try {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
          } catch (_) { /* ignore */ }
        }, 25000);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'pong') return;
          handleSignal(data);
        } catch (err) {
          console.warn('Call WS parse error', err);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        clearInterval(heartbeatTimer);
        if (!stopped) setTimeout(() => { if (!stopped) connect(); }, 1500);
      };

      ws.onerror = () => {};
      wsRef.current = ws;
    };

    connect();
    return () => {
      stopped = true;
      clearInterval(heartbeatTimer);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, [userId, token]); // eslint-disable-line

  // ── Call-duration timer ──
  useEffect(() => {
    if (callState === 'active') {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [callState]);

  // ── Cleanup (uses refs so it's always current) ──
  const cleanup = useCallback(() => {
    try { localStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
    try { if (pcRef.current?.close) pcRef.current.close(); } catch (_) {}
    pcRef.current = null;
    pendingCandidates.current = [];
    setLocalStream(null);
    setRemoteStream(null);
    setCallState('idle');
    setCallType(null);
    setPeerId(null);
    setPeerName(null);
    setIsIncoming(false);
    setElapsed(0);
    setMuted(false);
    setVideoOff(false);
    setAudioBlocked(false);
  }, []);

  // ── Signal handler (stable via ref, reads always-current pcRef) ──
  const handleSignal = useCallback((data) => {
    console.log('[Call] ← signal:', data.type);
    switch (data.type) {
      case 'incoming_call':
        setPeerId(data.caller_id);
        setPeerName(data.caller_name);
        setCallType(data.call_type);
        setIsIncoming(true);
        setCallState('ringing');
        pendingCandidates.current = [];
        // Stash the offer on a marker object; replaced with a real RTCPeerConnection on acceptCall
        pcRef.current = { pendingOffer: data.sdp };
        break;

      case 'call_answered':
        // Must be an actual RTCPeerConnection at this point
        if (pcRef.current && typeof pcRef.current.setRemoteDescription === 'function') {
          pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp))
            .then(() => {
              // Flush any ICE candidates that arrived BEFORE the answer did
              const queued = pendingCandidates.current;
              pendingCandidates.current = [];
              queued.forEach(c => {
                pcRef.current?.addIceCandidate(new RTCIceCandidate(c)).catch(err => {
                  console.warn('addIceCandidate (queued) failed:', err?.name || err);
                });
              });
            })
            .catch(err => console.error('setRemoteDescription (answer) failed:', err));
        }
        setCallState('active');
        break;

      case 'ice_candidate':
        if (data.candidate) {
          const pc = pcRef.current;
          // Only add immediately if pc is a real RTCPeerConnection AND has a remoteDescription set.
          // Otherwise buffer — addIceCandidate before SRD raises InvalidStateError and silently drops the candidate.
          if (pc && typeof pc.addIceCandidate === 'function' && pc.remoteDescription) {
            pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(err => {
              console.warn('addIceCandidate failed:', err?.name || err);
            });
          } else {
            pendingCandidates.current.push(data.candidate);
          }
        }
        break;

      case 'call_rejected':
        toast.info('Call declined');
        cleanup();
        break;

      case 'call_ended':
        if (data.reason === 'disconnected') toast.info('Other user disconnected');
        cleanup();
        break;

      case 'call_error': {
        const msg = data.message || 'Call failed';
        toast.error(msg);
        console.warn('[Call] call_error:', msg);
        cleanup();
        break;
      }

      default:
        break;
    }
  }, [cleanup]);

  // ── Create peer connection (explicit transceivers guarantee both audio+video directions) ──
  const createPeerConnection = useCallback((stream, type) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (e) => {
      if (e.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          target_id: peerIdRef.current,
          candidate: e.candidate.toJSON(),
        }));
      }
    };

    // Combine all incoming tracks into ONE MediaStream so a single audio element can play everything
    const remote = new MediaStream();
    pc.ontrack = (e) => {
      e.streams[0]?.getTracks().forEach(t => {
        if (!remote.getTracks().find(x => x.id === t.id)) remote.addTrack(t);
      });
      // Fallback: if e.streams is empty, add the bare track
      if (!e.streams.length) remote.addTrack(e.track);
      setRemoteStream(remote);
    };

    pc.onconnectionstatechange = () => {
      console.log('[Call] pc.connectionState =', pc.connectionState);
      // Only cleanup on terminal state 'closed'. 'failed' can sometimes recover via ICE restart —
      // and prematurely calling cleanup here was contributing to the "auto-decline" perception.
      if (pc.connectionState === 'closed') {
        cleanup();
      }
      if (pc.connectionState === 'failed') {
        // Give ICE ~4s to recover; if still failed, tear down with a user-visible reason.
        setTimeout(() => {
          if (pcRef.current === pc && pc.connectionState === 'failed') {
            toast.error('Connection lost');
            cleanup();
          }
        }, 4000);
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[Call] pc.iceConnectionState =', pc.iceConnectionState);
    };

    // Add local tracks to the connection
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    // Ensure we're ready to RECEIVE both kinds of tracks even if our local stream lacks one
    // (e.g. audio-only sender → video receiver would never trigger video ontrack without this)
    try {
      const hasAudio = stream.getAudioTracks().length > 0;
      const hasVideo = stream.getVideoTracks().length > 0;
      if (!hasAudio) pc.addTransceiver('audio', { direction: 'recvonly' });
      if (type === 'video' && !hasVideo) pc.addTransceiver('video', { direction: 'recvonly' });
    } catch (_) { /* safe for older browsers */ }

    return pc;
  }, [cleanup]);

  // ── Start a call (caller side) ──
  const startCall = useCallback(async (targetId, targetName, type = 'video') => {
    if (callState !== 'idle') return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    try {
      const constraints = type === 'video'
        ? { video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } }
        : { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);
      setPeerId(targetId);
      setPeerName(targetName);
      setCallType(type);
      setIsIncoming(false);
      setCallState('calling');

      const pc = createPeerConnection(stream, type);
      pcRef.current = pc;

      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: type === 'video',
      });
      await pc.setLocalDescription(offer);

      wsRef.current.send(JSON.stringify({
        type: 'call_offer',
        target_id: targetId,
        call_type: type,
        sdp: pc.localDescription.toJSON(),
      }));
    } catch (err) {
      console.error('startCall failed:', err);
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast.error('Microphone/camera access denied. Please allow access and try again.');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        toast.error('No microphone or camera found on this device.');
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        toast.error('Your microphone/camera is in use by another app.');
      } else {
        toast.error('Could not start call: ' + (err?.message || name || 'unknown error'));
      }
      cleanup();
    }
  }, [callState, createPeerConnection, cleanup]);

  // ── Accept incoming call ──
  const acceptCall = useCallback(async () => {
    if (callState !== 'ringing' || !isIncoming) return;

    try {
      const constraints = callType === 'video'
        ? { video: { facingMode: 'user' }, audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } }
        : { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false };

      // iOS: requesting media in a user-gesture handler unlocks WebAudio/autoplay
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);

      const pendingOffer = pcRef.current?.pendingOffer;
      const pc = createPeerConnection(stream, callType);
      pcRef.current = pc;

      if (pendingOffer) {
        await pc.setRemoteDescription(new RTCSessionDescription(pendingOffer));
        pendingCandidates.current.forEach(c => {
          pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
        });
        pendingCandidates.current = [];
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      wsRef.current.send(JSON.stringify({
        type: 'call_answer',
        target_id: peerIdRef.current,
        sdp: pc.localDescription.toJSON(),
      }));

      setCallState('active');
    } catch (err) {
      console.error('acceptCall failed:', err);
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast.error('Microphone/camera access denied. Please allow access and try again.');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        toast.error('No microphone or camera found on this device.');
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        toast.error('Your microphone/camera is in use by another app.');
      } else {
        toast.error('Could not accept call: ' + (err?.message || name || 'unknown error'));
      }
      // Notify caller so they don't spin forever
      if (wsRef.current?.readyState === WebSocket.OPEN && peerIdRef.current) {
        wsRef.current.send(JSON.stringify({ type: 'call_reject', target_id: peerIdRef.current }));
      }
      cleanup();
    }
  }, [callState, isIncoming, callType, createPeerConnection, cleanup]);

  const rejectCall = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && peerIdRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'call_reject', target_id: peerIdRef.current }));
    }
    cleanup();
  }, [cleanup]);

  const endCall = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && peerIdRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'call_end', target_id: peerIdRef.current }));
    }
    cleanup();
  }, [cleanup]);

  const toggleMute = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    s.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setMuted(m => !m);
  }, []);

  const toggleVideo = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    s.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setVideoOff(v => !v);
  }, []);

  // CallOverlay tells us when the browser blocked audio autoplay so the UI can surface a tap-to-play button
  const markAudioBlocked = useCallback(() => setAudioBlocked(true), []);
  const markAudioReady = useCallback(() => setAudioBlocked(false), []);

  return (
    <CallContext.Provider value={{
      callState, callType, peerId, peerName, isIncoming,
      elapsed, localStream, remoteStream,
      muted, videoOff, wsConnected, audioBlocked,
      startCall, acceptCall, rejectCall, endCall,
      toggleMute, toggleVideo,
      markAudioBlocked, markAudioReady,
    }}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be inside CallProvider');
  return ctx;
}
